import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopy from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import useProjectOverviewApi, {
  ProjectOverviewData,
} from './useProjectOverviewApi';
import {
  SortableTable,
  ColumnsProp,
  SortableTableDataPoint,
} from '../../DataDisplay/SortableTable';
import { useResourceTranslator } from '../../apiForm/resource';
import { addHost } from '../../utils/urls';
import './ProjectOverview.css';
import DocumentTitle from '../../utilComponents/DocumentTitle';
import AppRoutes from '../../router/routes';
import SearchableProjectSelect from '../../utilComponents/SearchableProjectSelect';
import { ProjectConfig } from '../../project/useProjectApi';

function ProjectSearchFilter({
  projects,
  inputRef,
  label,
  onProjectChange,
  onFilterChange,
}: {
  projects: ProjectConfig[];
  inputRef: React.Ref<HTMLInputElement>;
  label: string;
  onProjectChange: (project: ProjectConfig | ProjectConfig[] | null) => void;
  onFilterChange: (filter: string) => void;
}) {
  return (
    <SearchableProjectSelect
      projects={projects}
      onChange={onProjectChange}
      onInputChange={(_event: unknown, value: string) => onFilterChange(value)}
      clearOnBlur={false}
      showLabel={false}
      sx={{ minWidth: { xs: 180, sm: 280 } }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          variant="outlined"
          size="small"
        />
      )}
    />
  );
}

export default function ProjectOverview() {
  const api = useProjectOverviewApi();
  const t = useResourceTranslator();
  const navigate = useNavigate();
  const projectSearchInputRef = useRef<HTMLInputElement>(null);

  const [projects, setProjects] = useState<ProjectConfig[]>();
  const headCells = useMemo<ColumnsProp>(
    () => [
      {
        key: 'id',
        isNumeric: false,
        disablePadding: true,
        displayName: t('projectOverview.tableColumns.id') || '',
      },
      {
        key: 'name',
        isNumeric: false,
        disablePadding: false,
        displayName: t('projectOverview.tableColumns.name') || '',
      },
    ],
    [t],
  );

  const fetch = useCallback(async () => {
    const receivedProjects: ProjectOverviewData = await api.getAllProjects({});
    setProjects(receivedProjects?.projects || []);
  }, [api]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        projectSearchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);

  const redirectToRetailerPortal = useCallback((projectId: string) => {
    const retailerPortalUrl = addHost(
      window.location,
      `/${projectId}`,
      'retailer',
    );
    window.location.assign(retailerPortalUrl);
  }, []);

  const handleProjectChange = useCallback(
    (project: ProjectConfig | ProjectConfig[] | null) => {
      if (!project || Array.isArray(project)) return;
      navigate(AppRoutes.ProjectsView(project.id));
    },
    [navigate],
  );

  return (
    <>
      <DocumentTitle translationID="headlines.projectOverview" />
      <div className="button-container">
        <Button onClick={() => navigate(AppRoutes.ProjectsNew)}>
          <AddIcon />
          {t('projectOverview.createProjectButton')}
        </Button>
      </div>
      <Paper>
        <SortableTable
          data={projects || []}
          columnsMetadata={headCells}
          title={t('projectOverview.tableHeader') || ''}
          filterComponent={ProjectSearchFilter}
          filterComponentProps={{
            projects: projects || [],
            inputRef: projectSearchInputRef,
            label: t('projectOverview.tableFilterPlaceholder'),
            onProjectChange: handleProjectChange,
          }}
          rowButtons={[
            {
              icon: <EditIcon fontSize="small" />,
              callback: (row: SortableTableDataPoint) => {
                navigate(AppRoutes.ProjectsView(row.id));
              },
              tooltip: 'editProject',
            },
            {
              icon: <ContentCopy fontSize="small" />,
              callback: (row: SortableTableDataPoint) => {
                navigate(AppRoutes.ProjectsClone(row.id));
              },
              tooltip: 'cloneProject',
            },
            {
              icon: <DesktopWindowsOutlinedIcon fontSize="small" />,
              callback: (row: SortableTableDataPoint) => {
                redirectToRetailerPortal(row.id);
              },
              tooltip: 'openProjectRetailerPortal',
            },
          ]}
        />
      </Paper>
    </>
  );
}
